import { CommonModule, DatePipe } from '@angular/common';
import { Component, OnInit, inject, signal } from '@angular/core';
import { FormBuilder, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { finalize } from 'rxjs';
import { ExpenseClaimApiService } from '../../../core/api-services/expense-claim-api.service';
import { ScholarshipApiService } from '../../../core/api-services/scholarship-api.service';
import { AuthorizationService } from '../../../core/authorization/authorization.service';
import { Permissions } from '../../../core/authorization/permissions';
import {
  BulkClaimDecisionResult,
  ClaimDecisionReasonCode,
  ClaimDetail,
  ClaimStatus,
  ClaimSummary,
} from '../../../core/models/expense-claim';
import { Scholarship } from '../../../core/models/scholarship';

/**
 * Reviewer and finance queue for student expense claims.
 *
 * Reviewers filter by scholarship or status, approve or reject (a rejection
 * always carries a reason), bulk decide selections, verify individual documents,
 * and once a claim is approved finance records that it has been paid.
 */
@Component({
  selector: 'app-admin-claims',
  standalone: true,
  imports: [CommonModule, DatePipe, FormsModule, ReactiveFormsModule],
  templateUrl: './claims.html',
  styleUrl: './claims.scss',
})
export class AdminClaimsComponent implements OnInit {
  private readonly api = inject(ExpenseClaimApiService);
  private readonly scholarshipApi = inject(ScholarshipApiService);
  private readonly fb = inject(FormBuilder);
  readonly authorization = inject(AuthorizationService);
  readonly permissions = Permissions;

  readonly statuses: ClaimStatus[] = [
    'SUBMITTED',
    'RESUBMITTED',
    'UNDER_REVIEW',
    'APPROVED',
    'REJECTED',
    'PAID',
    'CANCELLED',
  ];

  readonly reasonCodes: { value: ClaimDecisionReasonCode; label: string }[] = [
    { value: 'CLEARER_DOCUMENT_REQUIRED', label: 'A clearer document is required' },
    { value: 'INCORRECT_AMOUNT', label: 'Incorrect amount' },
    { value: 'NOT_ELIGIBLE', label: 'Not covered by the awarded benefits' },
    { value: 'DUPLICATE_CLAIM', label: 'Duplicate claim' },
    { value: 'INSUFFICIENT_BUDGET', label: 'Insufficient budget remaining' },
    { value: 'INCOMPLETE_DETAILS', label: 'Incomplete details' },
    { value: 'OTHER', label: 'Other' },
  ];

  readonly claims = signal<ClaimSummary[]>([]);
  readonly scholarships = signal<Scholarship[]>([]);
  readonly selected = signal<ClaimDetail | null>(null);
  readonly loading = signal(false);
  readonly busy = signal(false);
  readonly error = signal('');
  readonly message = signal('');
  readonly chosen = signal<Set<number>>(new Set<number>());
  readonly bulkResult = signal<BulkClaimDecisionResult | null>(null);

  readonly filters = this.fb.group({
    status: [''],
    scholarshipId: [null as number | null],
    search: [''],
  });

  readonly decisionForm = this.fb.group({
    reasonCode: ['CLEARER_DOCUMENT_REQUIRED' as ClaimDecisionReasonCode],
    reason: ['', Validators.required],
  });

  readonly bulkForm = this.fb.group({
    decision: ['Approve' as 'Approve' | 'Reject'],
    reasonCode: ['CLEARER_DOCUMENT_REQUIRED' as ClaimDecisionReasonCode],
    reason: [''],
  });

  readonly paidForm = this.fb.group({
    paidAmount: [null as number | null],
    paymentReference: [''],
    paidDate: [''],
    notes: [''],
  });

  ngOnInit(): void {
    this.load();
    this.scholarshipApi.getScholarships().subscribe({
      next: (scholarships) => this.scholarships.set(scholarships ?? []),
      error: () => this.scholarships.set([]),
    });
  }

  load(): void {
    const value = this.filters.getRawValue();
    this.loading.set(true);
    this.error.set('');
    this.chosen.set(new Set<number>());

    this.api
      .getClaimsForReview(
        (value.status as ClaimStatus) || null,
        value.scholarshipId ?? null,
        null,
        value.search ?? ''
      )
      .pipe(finalize(() => this.loading.set(false)))
      .subscribe({
        next: (claims) => this.claims.set(claims ?? []),
        error: () => this.error.set('The claim queue could not be loaded.'),
      });
  }

  open(claim: ClaimSummary): void {
    this.error.set('');
    this.message.set('');

    this.api.getClaimForReview(claim.id).subscribe({
      next: (detail) => {
        this.selected.set(detail);
        this.decisionForm.reset({ reasonCode: 'CLEARER_DOCUMENT_REQUIRED', reason: '' });
        this.paidForm.reset({
          paidAmount: detail.amount,
          paymentReference: '',
          paidDate: '',
          notes: '',
        });
      },
      error: () => this.error.set('This claim could not be loaded.'),
    });
  }

  close(): void {
    this.selected.set(null);
  }

  toggle(id: number): void {
    this.chosen.update((chosen) => {
      const next = new Set(chosen);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  }

  isChosen(id: number): boolean {
    return this.chosen().has(id);
  }

  toggleAll(): void {
    const all = this.claims().map((claim) => claim.id);
    this.chosen.set(this.chosen().size === all.length ? new Set<number>() : new Set(all));
  }

  /** Approve or reject a single claim. A rejection requires a reason. */
  decide(decision: 'Approve' | 'Reject'): void {
    const claim = this.selected();
    if (!claim || this.busy()) {
      return;
    }

    const value = this.decisionForm.getRawValue();

    if (decision === 'Reject' && !(value.reason ?? '').trim()) {
      this.decisionForm.markAllAsTouched();
      this.error.set('A reason is required when rejecting a claim.');
      return;
    }

    this.busy.set(true);
    this.error.set('');
    this.message.set('');

    this.api
      .decide(claim.id, {
        decision,
        reasonCode: decision === 'Reject' ? value.reasonCode ?? null : null,
        reason: decision === 'Reject' ? value.reason ?? null : null,
      })
      .pipe(finalize(() => this.busy.set(false)))
      .subscribe({
        next: (updated) => {
          this.selected.set(updated);
          this.message.set(
            decision === 'Approve'
              ? `Claim ${updated.referenceNumber} approved and awaiting payment.`
              : `Claim ${updated.referenceNumber} rejected. The student can resubmit with a new document.`
          );
          this.load();
        },
        error: (err) => this.error.set(err?.error?.message || 'The claim could not be decided.'),
      });
  }

  /** Applies the bulk decision to every selected claim. */
  bulkDecide(): void {
    const ids = Array.from(this.chosen());
    if (ids.length === 0 || this.busy()) {
      return;
    }

    const value = this.bulkForm.getRawValue();

    if (value.decision === 'Reject' && !(value.reason ?? '').trim()) {
      this.error.set('A reason is required when rejecting claims.');
      return;
    }

    this.busy.set(true);
    this.error.set('');
    this.message.set('');
    this.bulkResult.set(null);

    this.api
      .bulkDecide(ids, {
        decision: value.decision ?? 'Approve',
        reasonCode: value.decision === 'Reject' ? value.reasonCode ?? null : null,
        reason: value.decision === 'Reject' ? value.reason ?? null : null,
      })
      .pipe(finalize(() => this.busy.set(false)))
      .subscribe({
        next: (result) => {
          this.bulkResult.set(result);
          this.message.set(`${result.succeeded} of ${result.requested} claims updated.`);
          this.load();
        },
        error: (err) => this.error.set(err?.error?.message || 'The bulk decision failed.'),
      });
  }

  /** Records settlement of an approved claim. */
  markPaid(): void {
    const claim = this.selected();
    if (!claim || this.busy()) {
      return;
    }

    const value = this.paidForm.getRawValue();
    this.busy.set(true);
    this.error.set('');
    this.message.set('');

    this.api
      .markPaid(claim.id, {
        paidAmount: value.paidAmount ?? null,
        paymentReference: value.paymentReference || null,
        paidDate: value.paidDate || null,
        notes: value.notes || null,
      })
      .pipe(finalize(() => this.busy.set(false)))
      .subscribe({
        next: (updated) => {
          this.selected.set(updated);
          this.message.set(`Claim ${updated.referenceNumber} marked as paid.`);
          this.load();
        },
        error: (err) => this.error.set(err?.error?.message || 'The payment could not be recorded.'),
      });
  }

  reviewDocument(documentId: number, decision: 'Approved' | 'Rejected'): void {
    const claim = this.selected();
    if (!claim) {
      return;
    }

    this.api.reviewDocument(claim.id, documentId, decision).subscribe({
      next: () => this.open(claim),
      error: () => this.error.set('The document review could not be saved.'),
    });
  }

  download(claim: ClaimDetail, documentId: number, fileName: string): void {
    this.api.downloadDocument(claim.id, documentId).subscribe({
      next: (blob) => {
        const url = window.URL.createObjectURL(blob);
        const anchor = window.document.createElement('a');
        anchor.href = url;
        anchor.download = fileName;
        window.document.body.appendChild(anchor);
        anchor.click();
        anchor.remove();
        window.URL.revokeObjectURL(url);
      },
      error: () => this.error.set('The document could not be downloaded.'),
    });
  }

  statusClass(status: string): string {
    switch (status) {
      case 'APPROVED':
      case 'PAID':
        return 'status status-approved';
      case 'REJECTED':
        return 'status status-rejected';
      case 'CANCELLED':
        return 'status status-cancelled';
      case 'RESUBMITTED':
        return 'status status-booked';
      default:
        return 'status status-pending';
    }
  }

  /** True when the claim is still awaiting a decision. */
  canDecide(claim: ClaimSummary): boolean {
    return ['SUBMITTED', 'RESUBMITTED', 'UNDER_REVIEW'].includes(claim.status);
  }
}
